import {
    Close as CloseIcon,
    SearchRounded,
    Visibility as VisibilityIcon,
} from "@mui/icons-material";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { firebase } from "common/src/config/configureFirebase";
import { get } from "firebase/database";
import { APP_CONFIG } from "./../../constants";

const AiSupport = () => {
    const { t } = useTranslation();
    const [conversations, setConversations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedConversation, setSelectedConversation] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    // Conversation dialog states
    const [conversationMessages, setConversationMessages] = useState([]);
    const [conversationLoading, setConversationLoading] = useState(false);
    const [conversationError, setConversationError] = useState(null);
    const [hasMoreMessages, setHasMoreMessages] = useState(false);
    const [currentOffset, setCurrentOffset] = useState(0);
    const [loadingMore, setLoadingMore] = useState(false);

    const { userByPhoneRef } = firebase;

    async function getUserByPhone(phone) {
        const snap = await get(userByPhoneRef(phone));
        if (snap.exists()) {
            console.log("User found ======> ", snap.val());

            const user = Object.values(snap.val())[0];

            return user;
        }
        return null;
    }

    // Fetch support logs from API
    const fetchSupportLogs = useCallback(async (enrichWithUserData = true) => {
        try {
            setLoading(true);
            setError(null);

            const response = await fetch(
                `${APP_CONFIG.whatsapp_ai_url}/chat-history/list-support-logs`,
                {
                    method: "GET",
                    headers: {
                        "x-key": "a5af540687e5f73a00ad4a6a7545b5cc1ba1fa4c",
                        "Content-Type": "application/json",
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const supportLogs = await response.json();
            console.log("Support logs from API:", supportLogs);

            // Filter and enrich logs with user data
            const enrichedConversations = [];

            if (Array.isArray(supportLogs)) {
                for (const log of supportLogs) {
                    // Skip logs with invalid phone numbers
                    if (!log.number_user || log.number_user === "status") {
                        continue;
                    }

                    // Find user by phone number (with flexible matching) - only if enrichment is requested and userdata is available
                    let user = null;
                    if (enrichWithUserData) {
                        user = await getUserByPhone(`+${log.number_user}`);
                    }

                    if (!user) {
                        continue;
                    }

                    // Always add the log, even if user is not found
                    enrichedConversations.push({
                        id: log.logNumber,
                        customerName: user
                            ? `${user.firstName || ""} ${
                                  user.lastName || ""
                              }`.trim() || "Unknown User"
                            : `User ${log.number_user}`, // Show phone number if user not found
                        customerEmail: user
                            ? user.email || "No email"
                            : "No email",
                        customerPhone: log.number_user, // Use original phone from log
                        date: log.created_at,
                        status: "completed", // Default status since API doesn't provide this
                        userId: user ? user.id : null,
                        hasUser: !!user, // Flag to indicate if user was found
                        messages: [], // Will be populated when viewing conversation details
                    });
                }
            }

            setConversations(enrichedConversations);
        } catch (err) {
            console.error("Error fetching support logs:", err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Disable exhaustive-deps to prevent infinite loop - userdata.users is handled via parameter

    useEffect(() => {
        // Initial fetch of support logs without user enrichment
        fetchSupportLogs(true);
    }, [fetchSupportLogs]);

    // Re-fetch when user data becomes available to enrich existing logs
    const hasUserDataRef = useRef(false);
    useEffect(() => {
        if (!hasUserDataRef.current) {
            console.log(
                "🔄 User data loaded for first time, re-enriching conversations..."
            );
            hasUserDataRef.current = true;
            fetchSupportLogs(true); // Enrich with user data
        }
    }, [fetchSupportLogs]); // Only re-fetch when user data first becomes available

    // Fetch conversation history from API
    const fetchConversationHistory = useCallback(
        async (phoneNumber, offset = 0, isLoadMore = false) => {
            try {
                if (!isLoadMore) {
                    setConversationLoading(true);
                    setConversationError(null);
                    setConversationMessages([]);
                } else {
                    setLoadingMore(true);
                }

                const response = await fetch(
                    `${APP_CONFIG.whatsapp_ai_url}/chat-history/history`,
                    {
                        method: "POST",
                        headers: {
                            "x-key": "a5af540687e5f73a00ad4a6a7545b5cc1ba1fa4c",
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                            number_user: phoneNumber,
                            service: "support",
                            limit: 20,
                            offset: offset,
                        }),
                    }
                );

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const messages = await response.json();
                console.log("Conversation messages from API:", messages);

                // Transform API response to match our UI format
                const transformedMessages = messages.map((msg) => ({
                    id: msg.id,
                    type: msg.type_message, // 'user' or 'ai'
                    message: msg.message_user,
                    timestamp: new Date(msg.created_at).toLocaleTimeString(
                        "es-ES",
                        {
                            hour: "2-digit",
                            minute: "2-digit",
                            second: "2-digit",
                        }
                    ),
                    fullDate: msg.created_at,
                    bookingId: msg.booking_id,
                }));

                if (isLoadMore) {
                    // Append new messages to existing ones
                    setConversationMessages((prev) => [
                        ...prev,
                        ...transformedMessages,
                    ]);
                } else {
                    // Replace messages for initial load
                    setConversationMessages(transformedMessages);
                }

                // Update pagination state
                setHasMoreMessages(messages.length === 20);
                setCurrentOffset(offset + messages.length);
            } catch (err) {
                console.error("Error fetching conversation history:", err);
                setConversationError(err.message);
            } finally {
                setConversationLoading(false);
                setLoadingMore(false);
            }
        },
        []
    );

    const handleViewConversation = (conversation) => {
        setSelectedConversation(conversation);
        setDialogOpen(true);
        setCurrentOffset(0);

        // Fetch conversation history
        if (conversation.customerPhone) {
            fetchConversationHistory(
                conversation.customerPhone.replaceAll("+", ""),
                0,
                true
            );
        }
    };

    const handleLoadMoreMessages = () => {
        if (
            selectedConversation?.customerPhone &&
            hasMoreMessages &&
            !loadingMore
        ) {
            fetchConversationHistory(
                selectedConversation.customerPhone.replaceAll("+", ""),
                currentOffset,
                true
            );
        }
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setSelectedConversation(null);
        setConversationMessages([]);
        setConversationError(null);
        setCurrentOffset(0);
        setHasMoreMessages(false);
    };

    const filteredConversations = conversations?.filter(
        (conv) =>
            conv.customerName
                .toLowerCase()
                .includes(searchTerm.toLowerCase()) ||
            conv.customerEmail
                .toLowerCase()
                .includes(searchTerm.toLowerCase()) ||
            conv.id.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleRefresh = useCallback(() => {
        // Always fetch with user enrichment on manual refresh
        fetchSupportLogs(true);
    }, [fetchSupportLogs]);

    // const handleExport = () => {
    //   // Implement export functionality
    //   console.log('Exporting conversations...');
    // };

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h4" sx={{ mb: 1, fontWeight: "bold" }}>
                    {t("ai_support_logs")}
                </Typography>
                <Typography variant="body1" color="textSecondary">
                    {t("ai_support_description")}
                </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
                <TextField
                    label={t("search")}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    size="small"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchRounded />
                            </InputAdornment>
                        ),
                    }}
                    fullWidth
                />
            </Box>

            {/* Conversations Card */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 2,
                        }}
                    >
                        <Typography variant="h6">
                            {t("conversation_logs")} (
                            {filteredConversations.length})
                        </Typography>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={handleRefresh}
                            disabled={loading}
                        >
                            {loading
                                ? t("loading") || "Loading..."
                                : t("refresh") || "Refresh"}
                        </Button>
                    </Box>

                    {error && (
                        <Alert severity="error" sx={{ mb: 3 }}>
                            Error loading support logs: {error}
                            <Button
                                size="small"
                                onClick={handleRefresh}
                                sx={{ ml: 2 }}
                            >
                                Retry
                            </Button>
                        </Alert>
                    )}

                    {loading ? (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                py: 4,
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    ) : filteredConversations.length === 0 ? (
                        <Box sx={{ textAlign: "center", py: 4 }}>
                            <Typography>
                                {t("no_conversations_found")}
                            </Typography>
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                            }}
                        >
                            {filteredConversations.map((conversation) => (
                                <Card
                                    key={conversation.id}
                                    variant="outlined"
                                    sx={{
                                        "&:hover": {
                                            boxShadow: 2,
                                            cursor: "pointer",
                                        },
                                    }}
                                >
                                    <CardContent sx={{ py: 2 }}>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                width: "100%",
                                            }}
                                        >
                                            {/* Left side - Customer info and ID */}
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 3,
                                                    flex: 1,
                                                }}
                                            >
                                                {/* Customer Info */}
                                                <Box>
                                                    <Typography
                                                        variant="body1"
                                                        fontWeight="bold"
                                                        sx={{
                                                            color: conversation.hasUser
                                                                ? "inherit"
                                                                : "text.secondary",
                                                            fontStyle:
                                                                conversation.hasUser
                                                                    ? "normal"
                                                                    : "italic",
                                                        }}
                                                    >
                                                        {
                                                            conversation.customerName
                                                        }
                                                        {/* {!conversation.hasUser && (
                                                            <Typography
                                                                component="span"
                                                                variant="caption"
                                                                sx={{
                                                                    ml: 1,
                                                                    color: "warning.main",
                                                                }}
                                                            >
                                                                (Not in system)
                                                            </Typography>
                                                        )} */}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        color="textSecondary"
                                                    >
                                                        {
                                                            conversation.customerEmail
                                                        }
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        color="textSecondary"
                                                    >
                                                        {
                                                            conversation.customerPhone
                                                        }
                                                    </Typography>
                                                </Box>

                                                {/* Log ID */}
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                    sx={{
                                                        fontFamily: "monospace",
                                                        backgroundColor:
                                                            "#f5f5f5",
                                                        px: 1,
                                                        py: 0.5,
                                                        borderRadius: 1,
                                                    }}
                                                >
                                                    #{conversation.id}
                                                </Typography>
                                            </Box>

                                            {/* Right side - Date and Action */}
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 2,
                                                    flexShrink: 0,
                                                }}
                                            >
                                                {/* Date */}
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                >
                                                    {new Date(
                                                        conversation.date
                                                    ).toLocaleDateString(
                                                        "es-ES"
                                                    )}{" "}
                                                    {new Date(
                                                        conversation.date
                                                    ).toLocaleTimeString(
                                                        "es-ES",
                                                        {
                                                            hour: "2-digit",
                                                            minute: "2-digit",
                                                        }
                                                    )}
                                                </Typography>

                                                {/* Action Button */}
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={
                                                        <VisibilityIcon />
                                                    }
                                                    onClick={() =>
                                                        handleViewConversation(
                                                            conversation
                                                        )
                                                    }
                                                    sx={{
                                                        minWidth: "auto",
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    {t("view_conversation")}
                                                </Button>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            ))}
                        </Box>
                    )}
                </CardContent>
            </Card>

            {/* Conversation Detail Dialog */}
            <Dialog
                open={dialogOpen}
                onClose={handleCloseDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                        }}
                    >
                        <Typography variant="h6">
                            {t("conversation")} con{" "}
                            {selectedConversation?.customerName}
                        </Typography>
                        <IconButton onClick={handleCloseDialog} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ p: 2 }}>
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="body2" color="textSecondary">
                            <strong>Phone:</strong>{" "}
                            {selectedConversation?.customerPhone}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            <strong>Email:</strong>{" "}
                            {selectedConversation?.customerEmail}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            <strong>Log ID:</strong> {selectedConversation?.id}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            <strong>Date:</strong>{" "}
                            {selectedConversation?.date
                                ? new Date(
                                      selectedConversation.date
                                  ).toLocaleString("es-ES")
                                : "N/A"}
                        </Typography>
                    </Box>

                    {conversationError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            Error loading conversation: {conversationError}
                            <Button
                                size="small"
                                onClick={() =>
                                    selectedConversation?.customerPhone &&
                                    fetchConversationHistory(
                                        selectedConversation.customerPhone,
                                        0,
                                        false
                                    )
                                }
                                sx={{ ml: 2 }}
                            >
                                Retry
                            </Button>
                        </Alert>
                    )}

                    {conversationLoading ? (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                py: 4,
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                            }}
                        >
                            {conversationMessages.length > 0 ? (
                                <>
                                    {conversationMessages.map(
                                        (message, index) => (
                                            <Box key={message.id || index}>
                                                <Typography
                                                    variant="body2"
                                                    fontWeight="bold"
                                                    sx={{ mb: 0.5 }}
                                                >
                                                    {message.type === "user"
                                                        ? selectedConversation?.customerName
                                                        : t("ai") || "AI"}
                                                    :
                                                </Typography>
                                                <Box
                                                    sx={{
                                                        borderLeft: `3px solid ${
                                                            message.type ===
                                                            "user"
                                                                ? "#1976d2"
                                                                : "#4caf50"
                                                        }`,
                                                        paddingLeft: "1rem",
                                                        marginLeft: "0.5rem",
                                                        mb: 0.5,
                                                        backgroundColor:
                                                            message.type ===
                                                            "user"
                                                                ? "#f3f7ff"
                                                                : "#f1f8e9",
                                                        borderRadius: "4px",
                                                        p: 1,
                                                    }}
                                                >
                                                    <Typography variant="body2">
                                                        {message.message}
                                                    </Typography>
                                                </Box>
                                                <Typography
                                                    variant="caption"
                                                    color="textSecondary"
                                                >
                                                    {new Date(
                                                        message.fullDate
                                                    ).toLocaleDateString(
                                                        "es-ES"
                                                    )}{" "}
                                                    {message.timestamp}
                                                    {message.bookingId && (
                                                        <span
                                                            style={{
                                                                marginLeft:
                                                                    "8px",
                                                                fontWeight:
                                                                    "bold",
                                                            }}
                                                        >
                                                            | Booking:{" "}
                                                            {message.bookingId}
                                                        </span>
                                                    )}
                                                </Typography>
                                            </Box>
                                        )
                                    )}

                                    {hasMoreMessages && (
                                        <Box
                                            sx={{ textAlign: "center", mt: 2 }}
                                        >
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={handleLoadMoreMessages}
                                                disabled={loadingMore}
                                                startIcon={
                                                    loadingMore ? (
                                                        <CircularProgress
                                                            size={16}
                                                        />
                                                    ) : null
                                                }
                                            >
                                                {loadingMore
                                                    ? "Loading..."
                                                    : "Load More Messages"}
                                            </Button>
                                        </Box>
                                    )}
                                </>
                            ) : (
                                <Box sx={{ textAlign: "center", py: 4 }}>
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                    >
                                        No conversation messages found for this
                                        user.
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default AiSupport;
